import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, articleList } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading font-bold text-base md:text-lg text-foreground-900 whitespace-nowrap">
              Vigorbuy Spread
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setGuideDropdownOpen(false);
                }}
                className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200 py-3 max-h-96 overflow-y-auto z-50">
                  <p className="px-4 pb-2 text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">Product Categories</p>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-50 flex-shrink-0">
                        <i className={`${cat.icon} text-sm text-primary-500`}></i>
                      </div>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Guide Dropdown */}
            <div className="relative" ref={guideRef}>
              <button
                onClick={() => {
                  setGuideDropdownOpen(!guideDropdownOpen);
                  setDropdownOpen(false);
                }}
                className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200 py-3 max-h-80 overflow-y-auto z-50">
                  <p className="px-4 pb-2 text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">SEO Guides</p>
                  {articleList.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/guide/${article.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-primary-100 text-primary-600 whitespace-nowrap flex-shrink-0">
                        {article.category}
                      </span>
                      <span className="line-clamp-1">{article.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Spreadsheet
            </a>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className={`text-xl text-foreground-700 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            {/* Home */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-home-4-line text-base text-primary-500"></i>
              </div>
              Home
            </Link>

            {/* Categories Toggle */}
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-grid-line text-base text-primary-500"></i>
                </div>
                Categories
              </div>
              <i className={`ri-arrow-down-s-line text-base text-foreground-400 transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {/* Mobile Categories Submenu */}
            {mobileCatOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <div className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-50 flex-shrink-0">
                      <i className={`${cat.icon} text-xs text-primary-500`}></i>
                    </div>
                    <span className="whitespace-nowrap">{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-[10px] text-foreground-300 ml-auto"></i>
                  </a>
                ))}
              </div>
            )}

            {/* Shopping Guide Toggle */}
            <button
              onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
              className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-book-open-line text-base text-primary-500"></i>
                </div>
                Shopping Guide
              </div>
              <i className={`ri-arrow-down-s-line text-base text-foreground-400 transition-transform duration-200 ${mobileGuideOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {/* Mobile Guides Submenu */}
            {mobileGuideOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200 space-y-0.5">
                {articleList.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/guide/${article.slug}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-primary-100 text-primary-600 whitespace-nowrap flex-shrink-0">
                      {article.category}
                    </span>
                    <span className="line-clamp-1">{article.title}</span>
                  </Link>
                ))}
              </div>
            )}

            {/* Spreadsheet */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-file-list-3-line text-base text-primary-500"></i>
              </div>
              Spreadsheet
            </a>

            <div className="border-t border-background-200 my-2"></div>

            {/* Buy It Now */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 px-5 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full text-center transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}